import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, message, Space, Select, Popconfirm, Modal, Form, Input, Switch, Divider, Descriptions } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, SaveOutlined, WarningOutlined, CheckCircleOutlined, HistoryOutlined, ExperimentOutlined, AndroidOutlined, AppleOutlined, EditOutlined } from '@ant-design/icons';
import { getVersionRequirements, updateVersionRequirements, getVersionHistory, checkAppVersion, deleteVersion, updateVersion } from '../services/versionService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const VersionManagement = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentData, setCurrentData] = useState({ android: [], ios: [] });
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [addVersionModalVisible, setAddVersionModalVisible] = useState(false);
  const [addVersionForm] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editingVersion, setEditingVersion] = useState(null);

  useEffect(() => {
    fetchVersionRequirements();
    fetchVersionHistory();
  }, []);

  const fetchVersionRequirements = async () => {
    setLoading(true);
    try {
      const response = await getVersionRequirements();
      setCurrentData(response);
    } catch (error) {
      messageApi.error('Failed to fetch version requirements');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await getVersionHistory();
      setHistoryData(response.history || []);
    } catch (error) {
      messageApi.error('Failed to fetch version history');
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAddVersion = async (values) => {
    setSaving(true);
    try {
      await updateVersionRequirements(values);
      messageApi.success(`Version ${values.version} for ${values.platform} added successfully`);
      
      // Refresh data
      await fetchVersionRequirements();
      await fetchVersionHistory();
      
      // Close modal and reset form
      setAddVersionModalVisible(false);
      addVersionForm.resetFields();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add version';
      messageApi.error(errorMessage);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVersion = async (platform, version) => {
    try {
      await deleteVersion(platform, version);
      messageApi.success(`Version ${version} for ${platform} deleted successfully`);
      await fetchVersionRequirements();
    } catch (error) {
      messageApi.error('Failed to delete version');
      console.error(error);
    }
  };

  const handleEditVersion = (version) => {
    setEditingVersion(version);
    editForm.setFieldsValue({
      platform: version.platform,
      version: version.version,
      updateMessage: version.updateMessage,
      forceUpdate: version.forceUpdate
    });
    setEditModalVisible(true);
  };

  const handleUpdateVersion = async (values) => {
    setSaving(true);
    try {
      await updateVersion(values.platform, values.version, values.updateMessage, values.forceUpdate);
      messageApi.success(`Version ${values.version} for ${values.platform} updated successfully`);
      
      // Refresh data
      await fetchVersionRequirements();
      await fetchVersionHistory();
      
      // Close modal and reset form
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingVersion(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update version';
      messageApi.error(errorMessage);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestVersion = async (values) => {
    setTestLoading(true);
    try {
      const result = await checkAppVersion(values.testVersion, values.testPlatform);
      setTestResult(result);
      setTestModalVisible(true);
    } catch (error) {
      messageApi.error('Failed to test version');
      console.error(error);
    } finally {
      setTestLoading(false);
    }
  };

  const openAddVersionModal = () => {
    setAddVersionModalVisible(true);
    addVersionForm.resetFields();
  };

  const openTestModal = () => {
    setTestModalVisible(true);
    setTestResult(null);
    addVersionForm.setFieldsValue({
      testVersion: '',
      testPlatform: 'android'
    });
  };

  const validateVersionFormat = (_, value) => {
    if (!value) return Promise.resolve();
    
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(value)) {
      return Promise.reject(new Error('Use semantic versioning format (e.g., 1.0.0)'));
    }
    
    return Promise.resolve();
  };

  const versionColumns = [
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      render: (version) => <Tag color="blue">{version}</Tag>,
      sorter: (a, b) => {
        const partsA = a.version.split('.').map(Number);
        const partsB = b.version.split('.').map(Number);
        
        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
          const partA = partsA[i] || 0;
          const partB = partsB[i] || 0;
          
          if (partA < partB) return -1;
          if (partA > partB) return 1;
        }
        
        return 0;
      },
    },
    {
      title: 'Force Update',
      dataIndex: 'forceUpdate',
      key: 'forceUpdate',
      render: (forceUpdate) => (
        forceUpdate ? (
          <Tag color="red" icon={<WarningOutlined />}>Required</Tag>
        ) : (
          <Tag color="green" icon={<CheckCircleOutlined />}>Optional</Tag>
        )
      ),
      filters: [
        { text: 'Required', value: true },
        { text: 'Optional', value: false }
      ],
      onFilter: (value, record) => record.forceUpdate === value,
    },
    {
      title: 'Update Message',
      dataIndex: 'updateMessage',
      key: 'updateMessage',
      render: (message) => message || <Text type="secondary">No message</Text>,
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (timestamp) => dayjs(timestamp).format('DD MMM YYYY'),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditVersion(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete version ${record.version}?`}
            description="This action cannot be undone."
            onConfirm={() => handleDeleteVersion(record.platform, record.version)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => dayjs(timestamp).format('DD MMM YYYY, HH:mm'),
      sorter: (a, b) => dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag color="blue">{action}</Tag>
      ),
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform) => (
        <Tag color={platform === 'android' ? 'green' : 'blue'}>
          {platform === 'android' ? <AndroidOutlined /> : <AppleOutlined />} {platform}
        </Tag>
      ),
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      render: (version) => <Tag color="blue">{version}</Tag>,
    },
    {
      title: 'Force Update',
      dataIndex: 'forceUpdate',
      key: 'forceUpdate',
      render: (forceUpdate) => (
        forceUpdate ? (
          <Tag color="red">Required</Tag>
        ) : (
          <Tag color="green">Optional</Tag>
        )
      ),
    },
    {
      title: 'Updated By',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {contextHolder}
      
      <Title level={2}>
        <InfoCircleOutlined style={{ marginRight: '8px' }} />
        App Version Management
      </Title>

      <Paragraph>
        Manage app versions for Android and iOS platforms. Add new versions with force update flags to control when users must update their apps.
      </Paragraph>

      <Row gutter={[24, 24]}>
        {/* Add New Version */}
        <Col xs={24} lg={8}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddVersionModal}
                block
              >
                Add New Version
              </Button>
              <Button
                icon={<ExperimentOutlined />}
                onClick={openTestModal}
                block
              >
                Test Version Check
              </Button>
              <Button
                icon={<HistoryOutlined />}
                onClick={fetchVersionHistory}
                loading={historyLoading}
                block
              >
                Refresh History
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Version Statistics */}
        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <AndroidOutlined style={{ fontSize: '24px', color: themeConfig.status.success, marginBottom: '8px' }} />
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Android Versions</div>
                  <div style={{ fontSize: '24px', color: themeConfig.status.success }}>{currentData.android?.length || 0}</div>
                  <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
                    {currentData.android?.filter(v => v.forceUpdate).length || 0} force updates
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <div style={{ textAlign: 'center' }}>
                  <AppleOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>iOS Versions</div>
                  <div style={{ fontSize: '24px', color: '#1890ff' }}>{currentData.ios?.length || 0}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {currentData.ios?.filter(v => v.forceUpdate).length || 0} force updates
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Android Versions */}
      <Card
        title={
          <Space>
            <AndroidOutlined style={{ color: themeConfig.status.success }} />
            Android Versions
          </Space>
        }
        style={{ marginTop: '24px' }}
      >
        <Table
          columns={versionColumns}
          dataSource={currentData.android || []}
          loading={loading}
          rowKey={(record) => record.SK}
          pagination={false}
          locale={{
            emptyText: 'No Android versions configured yet'
          }}
        />
      </Card>

      {/* iOS Versions */}
      <Card
        title={
          <Space>
            <AppleOutlined style={{ color: themeConfig.status.info }} />
            iOS Versions
          </Space>
        }
        style={{ marginTop: '24px' }}
      >
        <Table
          columns={versionColumns}
          dataSource={currentData.ios || []}
          loading={loading}
          rowKey={(record) => record.SK}
          pagination={false}
          locale={{
            emptyText: 'No iOS versions configured yet'
          }}
        />
      </Card>

      {/* Version History */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            Version Update History
          </Space>
        }
        style={{ marginTop: '24px' }}
      >
        <Table
          columns={historyColumns}
          dataSource={historyData}
          loading={historyLoading}
          rowKey={(record) => record.SK}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* Add Version Modal */}
      <Modal
        title="Add New Version"
        open={addVersionModalVisible}
        onCancel={() => setAddVersionModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={addVersionForm}
          layout="vertical"
          onFinish={handleAddVersion}
          initialValues={{
            platform: 'android',
            forceUpdate: false
          }}
        >
          <Form.Item
            name="platform"
            label="Platform"
            rules={[{ required: true, message: 'Please select platform' }]}
          >
            <Select placeholder="Select platform">
              <Select.Option value="android">
                <Space>
                  <AndroidOutlined style={{ color: themeConfig.status.success }} />
                  Android
                </Space>
              </Select.Option>
              <Select.Option value="ios">
                <Space>
                  <AppleOutlined style={{ color: themeConfig.status.info }} />
                  iOS
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="version"
            label="Version Number"
            rules={[
              { required: true, message: 'Please enter version number' },
              { validator: validateVersionFormat }
            ]}
          >
            <Input placeholder="e.g., 1.3.0" />
          </Form.Item>

          <Form.Item
            name="forceUpdate"
            label="Force Update Required"
            tooltip="When enabled, users below this version will be blocked from using the app until they update"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="updateMessage"
            label="Update Message"
            tooltip="Optional message to show users about this update"
          >
            <TextArea
              rows={3}
              placeholder="Describe what's new in this version..."
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setAddVersionModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Add Version
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Version Modal */}
      <Modal
        title={`Edit Version ${editingVersion?.version} for ${editingVersion?.platform}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingVersion(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateVersion}
        >
          <Form.Item
            name="platform"
            label="Platform"
            rules={[{ required: true, message: 'Please select platform' }]}
          >
            <Select disabled>
              <Select.Option value="android">Android</Select.Option>
              <Select.Option value="ios">iOS</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="version"
            label="Version"
            rules={[
              { required: true, message: 'Please enter version' },
              { validator: validateVersionFormat }
            ]}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="forceUpdate"
            label="Force Update Required"
            tooltip="When enabled, users below this version will be blocked from using the app until they update"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="updateMessage"
            label="Update Message"
            tooltip="Optional message to show users about this update"
          >
            <TextArea
              rows={3}
              placeholder="Describe what's new in this version..."
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setEditModalVisible(false);
                setEditingVersion(null);
                editForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Update Version
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Test Version Modal */}
      <Modal
        title="Test Version Check"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <Form layout="vertical" onFinish={handleTestVersion}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="testVersion"
                label="Test Version"
                rules={[
                  { required: true, message: 'Please enter a test version' },
                  { validator: validateVersionFormat }
                ]}
              >
                <Input placeholder="e.g., 1.0.0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="testPlatform"
                label="Platform"
                rules={[{ required: true, message: 'Please select platform' }]}
              >
                <Select placeholder="Select platform">
                  <Select.Option value="android">Android</Select.Option>
                  <Select.Option value="ios">iOS</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={testLoading}
              block
            >
              Test Version
            </Button>
          </Form.Item>
        </Form>

        {testResult && (
          <div style={{ marginTop: '16px' }}>
            <Divider>Test Results</Divider>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Test Version">
                <Tag color="blue">{testResult.currentVersion}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Platform">
                <Tag color="purple">{testResult.platform}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Needs Update">
                {testResult.needsUpdate ? (
                  <Tag color="orange">Yes</Tag>
                ) : (
                  <Tag color="green">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Force Update">
                {testResult.forceUpdate ? (
                  <Tag color="red">Yes</Tag>
                ) : (
                  <Tag color="green">No</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Latest Version">
                <Tag color="blue">{testResult.latestVersion}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Update Message">
                {testResult.updateMessage || <Text type="secondary">No message</Text>}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VersionManagement;
